import { Link } from "@tanstack/react-router";

import {
  BIBLE_BOOKS,
  type BibleBook,
  bibleMinutes,
  GENRE_LABEL,
} from "../lib/bible";
import { passageBySlug } from "../lib/passage";
import { verseTopicBySlug } from "../lib/verses";
import { FaqList } from "./ProseBlocks";
import { StoreCta } from "./StoreCta";

/**
 * One book of the Bible.
 *
 * ── Why the facts are a table and the rest is prose ──────────────────────────
 *
 * Because they are answering two different searches. "How many chapters in
 * Jeremiah" wants a number visible without reading, and "what is the book of
 * Jeremiah about" wants a paragraph. A page that buries the number in a
 * sentence fails the first; a page that is only a table fails the second.
 *
 * ── Why the neighbours are at the bottom ─────────────────────────────────────
 *
 * Sixty-six pages with no links between them are sixty-six leaves. Previous and
 * next in canonical order gives every book two inbound links from pages that
 * are genuinely related, which is the cheapest real internal linking available
 * and the reason a crawler will find Zephaniah at all.
 */

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="border-t-[2.5px] border-ink py-3 first:border-t-0 sm:py-4">
      <dt className="font-display text-xs font-bold tracking-[0.16em] text-scripture uppercase">
        {label}
      </dt>
      <dd className="mt-1 leading-[1.6] text-body">{value}</dd>
    </div>
  );
}

function Neighbours({ book }: { book: BibleBook }) {
  const previous = BIBLE_BOOKS.find((b) => b.order === book.order - 1);
  const next = BIBLE_BOOKS.find((b) => b.order === book.order + 1);
  if (!previous && !next) return null;
  return (
    <nav
      aria-label="Neighbouring books"
      className="mt-12 grid gap-4 border-t-[2.5px] border-ink pt-8 sm:grid-cols-2"
    >
      {previous ? (
        <Link
          to="/bible/$slug"
          params={{ slug: previous.slug }}
          className="brutal flex flex-col gap-1 p-5 transition-transform hover:-translate-y-0.5"
        >
          <span className="font-display text-xs font-bold tracking-[0.16em] text-scripture uppercase">
            Before it
          </span>
          <span className="font-display text-lg font-bold text-ink">
            {previous.name}
          </span>
        </Link>
      ) : (
        <span />
      )}
      {next ? (
        <Link
          to="/bible/$slug"
          params={{ slug: next.slug }}
          className="brutal flex flex-col gap-1 p-5 transition-transform hover:-translate-y-0.5 sm:text-right"
        >
          <span className="font-display text-xs font-bold tracking-[0.16em] text-scripture uppercase">
            After it
          </span>
          <span className="font-display text-lg font-bold text-ink">
            {next.name}
          </span>
        </Link>
      ) : null}
    </nav>
  );
}

export function BibleBookPage({ book }: { book: BibleBook }) {
  const passages = (book.passages ?? [])
    .map(passageBySlug)
    .filter((p) => p !== undefined);
  const topics = (book.topics ?? [])
    .map(verseTopicBySlug)
    .filter((t) => t !== undefined);

  return (
    <article className="mx-auto w-full max-w-[68ch]">
      <p className="font-display text-xs font-bold tracking-[0.16em] text-scripture uppercase">
        {book.testament === "old" ? "Old Testament" : "New Testament"} ·{" "}
        {GENRE_LABEL[book.genre]} · book {book.order} of 66 ·{" "}
        {bibleMinutes(book)} min read
      </p>
      <h1 className="mt-3 font-display text-[clamp(1.9rem,6vw,2.9rem)] leading-[1.15] font-bold tracking-tight text-ink text-balance">
        The book of {book.name}
      </h1>
      <p className="mt-4 text-[clamp(1.05rem,2.4vw,1.2rem)] leading-relaxed text-ink2">
        {book.argument}
      </p>

      <figure className="mt-8 border-l-[3px] border-scripture bg-scripture-wash/60 py-5 pr-5 pl-6">
        <blockquote className="font-display text-[clamp(1.05rem,2.5vw,1.25rem)] leading-snug text-scripture">
          “{book.known.text}”
        </blockquote>
        <figcaption className="mt-3 font-display text-sm font-bold tracking-wide text-scripture uppercase">
          {book.known.ref}
        </figcaption>
      </figure>

      <section className="mt-10">
        <h2
          id="at-a-glance"
          className="scroll-mt-24 font-display text-[clamp(1.4rem,3.5vw,1.9rem)] leading-tight font-bold text-ink"
        >
          At a glance
        </h2>
        <dl className="brutal mt-5 px-5 py-1 sm:px-6">
          <Fact label="Chapters" value={String(book.chapters)} />
          <Fact label="Author" value={book.author} />
          <Fact label="Written" value={book.written} />
          <Fact label="Written for" value={book.audience} />
          <Fact label="Themes" value={book.themes.join(", ")} />
        </dl>
      </section>

      <section className="mt-12">
        <h2
          id="what-it-is-about"
          className="scroll-mt-24 font-display text-[clamp(1.4rem,3.5vw,1.9rem)] leading-tight font-bold text-ink"
        >
          What it is about
        </h2>
        {book.summary.map((paragraph) => (
          <p
            key={paragraph.slice(0, 48)}
            className="mt-4 leading-[1.75] text-body"
          >
            {paragraph}
          </p>
        ))}
      </section>

      <section className="mt-12">
        <h2
          id="where-to-start"
          className="scroll-mt-24 font-display text-[clamp(1.4rem,3.5vw,1.9rem)] leading-tight font-bold text-ink"
        >
          Where to start
        </h2>
        <p className="mt-4 leading-[1.75] text-body">{book.startHere}</p>
        <h3 className="mt-8 font-display text-xl font-bold text-ink">
          The chapters worth knowing
        </h3>
        <ul className="mt-4 flex flex-col gap-3">
          {book.keyChapters.map((entry) => (
            <li
              key={entry.chapter}
              className="border-l-[3px] border-brand pl-4 leading-[1.75] text-body"
            >
              <span className="font-display font-bold text-ink">
                {book.name} {entry.chapter}
              </span>{" "}
              — {entry.why}
            </li>
          ))}
        </ul>
      </section>

      <section className="mt-12">
        <h2
          id="what-people-get-wrong"
          className="scroll-mt-24 font-display text-[clamp(1.4rem,3.5vw,1.9rem)] leading-tight font-bold text-ink"
        >
          What people get wrong about it
        </h2>
        <p className="mt-4 leading-[1.75] text-body">{book.misread}</p>
      </section>

      {passages.length > 0 || topics.length > 0 ? (
        <section className="mt-12">
          <h2 className="font-display text-2xl font-bold text-ink">
            From this book
          </h2>
          <ul className="mt-5 grid gap-4 sm:grid-cols-2">
            {passages.map((passage) => (
              <li key={passage.slug}>
                <Link
                  to="/verse/$slug"
                  params={{ slug: passage.slug }}
                  className="brutal flex h-full flex-col gap-2 p-5 transition-transform hover:-translate-y-0.5"
                >
                  <span className="font-display text-lg leading-snug font-bold text-ink">
                    {passage.h1}
                  </span>
                  <span className="text-sm leading-relaxed text-ink2">
                    {passage.description}
                  </span>
                </Link>
              </li>
            ))}
            {topics.map((topic) => (
              <li key={topic.slug}>
                <Link
                  to="/verses/$slug"
                  params={{ slug: topic.slug }}
                  className="brutal flex h-full flex-col gap-2 p-5 transition-transform hover:-translate-y-0.5"
                >
                  <span className="font-display text-lg leading-snug font-bold text-ink">
                    {topic.h1}
                  </span>
                  <span className="text-sm leading-relaxed text-ink2">
                    {topic.description}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {book.faq ? <FaqList items={book.faq} /> : null}

      <StoreCta from={`bible:${book.slug}`} />

      <Neighbours book={book} />
    </article>
  );
}
